import {get, post} from '@/services/infrastructure/api.service'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import {DateRange} from '@/dateUtils/DateRange'
import {lastDayOfMonth} from 'date-fns'
import {Period, VatOptionComment} from '@/vat/types/VatReport'
import {VatPeriod} from '@/vat/types/VatPeriod'

const PATH_VAT_REPORT = '/vat/reports'
const PATH_VAT_BALANCE = '/vat/balances'
const PATH_VAT_STATUSES = '/vat/statuses'
const PATH_TRANSACTION_VAT = '/transactions/vat'

export type VatReportResponse = {
    vatTotal: number
    receipt: string
    periodStart: string
    remark: string
    internalRemark: string
    comments: VatOptionComment[]
    documentMeta: DocumentMeta
    createdAt: string
}

export type VatOptionBalance = {
    vatOptionId: number
    sourceSum: number
    targetSum: number
    comment?: string
}

export type VatPeriodCommandBookkeep = {
    period: Period
    internalRemark?: string
    remark?: string
    comments: VatOptionComment[]
}

export type VatPeriodStatus = {
    startDate: string,
    endDate:   string,
    status: 'Completed' | 'DueReporting',
    periodType: 'Termly' | 'Yearly'
}

const fetchVatTermStatuses = async (): Promise<VatPeriodStatus[]> => {
    return await get(PATH_VAT_STATUSES) ?? []
}

const fetchVatBalances = async (fromDate: string, toDate: string): Promise<VatOptionBalance[]> => {
    return await get(PATH_VAT_BALANCE, {fromDate, toDate}) ?? []
}

const fetchVatReport = async (fromDate: string, toDate: string): Promise<VatReportResponse[]> => {
    return await get(PATH_VAT_REPORT, {
        fromDate,
        toDate
    }) ?? []
}

const bookkeepVatPeriod = async (cmd: VatPeriodCommandBookkeep): Promise<any> => {
    return post(PATH_TRANSACTION_VAT, cmd)
}


const vatPeriodByMonth = (fromMonth: number, toMonth: number) => {
    const matchingPeriod = vatPeriods.find(period =>
        period.month.fromDate == fromMonth.toString() && period.month.toDate == toMonth.toString())

    if (matchingPeriod) {
        return matchingPeriod
    }
}

const vatPeriods: VatPeriod[] = [
    {
        periodName: 'jan-feb',
        month: {
            fromDate: '1',
            toDate: '2'
        },
        paymentDue: '10. april'
    },
    {
        periodName: 'mar-apr',
        month: {
            fromDate: '3',
            toDate: '4'
        },
        paymentDue: '10. juni'
    },
    {
        periodName: 'may-jun',
        month: {
            fromDate: '5',
            toDate: '6'
        },
        paymentDue: '31. august'
    },
    {
        periodName: 'jul-aug',
        month: {
            fromDate: '7',
            toDate: '8'
        },
        paymentDue: '10. oktober'
    },
    {
        periodName: 'sep-oct',
        month: {
            fromDate: '9',
            toDate: '10'
        },
        paymentDue: '10. desember'
    },
    {
        periodName: 'nov-dec',
        month: {
            fromDate: '11',
            toDate: '12'
        },
        paymentDue: '10. februar'
    },
    {
        periodName: 'yearly',
        month: {
            fromDate: '1',
            toDate: '12'
        },
        paymentDue: '10. mars'
    },
]

const getPreviousTerm = (vatAnnually: boolean = false): DateRange => {
    const currentDate = new Date()
    if (vatAnnually) {
        return {
            start: new Date(currentDate.getFullYear() - 1, 0, 1),
            end: lastDayOfMonth(new Date(currentDate.getFullYear() - 1, 11, 1)),
        }
    }

    if (currentDate.getMonth() == 0 || currentDate.getMonth() == 1) {
        return {
            start: new Date(currentDate.getFullYear() - 1, 0, 1),
            end: lastDayOfMonth(new Date(currentDate.getFullYear() - 1, 1, 1)),
        }
    }

    const deltaMonth = currentDate.getMonth() % 2 === 1 ? -1 : -2
    const startMonth = currentDate.getMonth() + deltaMonth
    return {
        start: new Date(currentDate.getFullYear(), startMonth, 1),
        end: lastDayOfMonth(new Date(currentDate.getFullYear(), startMonth + 1, 1)),
    }
}

export {
    fetchVatReport,
    vatPeriodByMonth,
    fetchVatBalances,
    bookkeepVatPeriod,
    getPreviousTerm,
    fetchVatTermStatuses
}